import { useEffect, useState } from 'react';
import { api } from '../lib/api';

export type Capture = {
  id: number;
  title: string;
  kind: 'still' | 'turntable' | 'annotated';
  file_key: string;
  mime_type: string | null;
  width: number | null;
  height: number | null;
  duration_ms: number | null;
  document_id: number | null;
  version_id: number | null;
  created_at: string;
  created_by_name: string | null;
};

type Props = {
  projectId: number;
  selectable?: boolean;
  selectedIds?: Set<number>;
  onToggleSelect?: (id: number) => void;
};

export function fileUrl(key: string): string {
  return `/api/v1/files?key=${encodeURIComponent(key)}`;
}

export default function CapturesGallery({
  projectId,
  selectable = false,
  selectedIds,
  onToggleSelect,
}: Props) {
  const [captures, setCaptures] = useState<Capture[]>([]);
  const [preview, setPreview] = useState<Capture | null>(null);
  const [loading, setLoading] = useState(true);

  async function refresh() {
    setLoading(true);
    try {
      const r = await api.get<{ captures: Capture[] }>(`/projects/${projectId}/captures`);
      setCaptures(Array.isArray(r?.captures) ? r.captures : []);
    } catch {
      setCaptures([]);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    refresh();
  }, [projectId]);

  async function handleDelete(id: number) {
    if (!confirm('Supprimer cette capture ?')) return;
    await api.del(`/captures/${id}`);
    refresh();
  }

  async function handleRename(c: Capture) {
    const t = prompt('Nouveau titre', c.title);
    if (!t || t === c.title) return;
    await api.patch(`/captures/${c.id}`, { title: t });
    refresh();
  }

  return (
    <div>
      {loading && <div className="card card-meta">Chargement de la galerie…</div>}
      {!loading && captures.length === 0 && (
        <div className="card card-meta">
          Aucune capture pour l'instant. Ouvre un document IFC et utilise 📸 Capturer dans la toolbar.
        </div>
      )}
      <div className="gallery-grid">
        {captures.map((c) => {
          const isVideo = c.kind === 'turntable' || (c.mime_type ?? '').startsWith('video');
          const src = fileUrl(c.file_key);
          const selected = selectedIds?.has(c.id) ?? false;
          return (
            <div
              key={c.id}
              className="gallery-card"
              onClick={() => {
                if (selectable && onToggleSelect) onToggleSelect(c.id);
                else setPreview(c);
              }}
              style={{
                outline: selected ? '3px solid var(--c-sage)' : 'none',
                outlineOffset: -3,
              }}
            >
              <span className="kind-chip">
                {c.kind === 'turntable' ? '360°' : c.kind === 'annotated' ? 'Annotée' : 'Vue'}
              </span>
              {isVideo ? (
                <video className="thumb" src={src} muted preload="metadata" />
              ) : (
                <img className="thumb" src={src} alt={c.title} />
              )}
              <div className="meta">
                <div style={{ fontWeight: 600, fontSize: 13 }}>{c.title}</div>
                <div className="card-meta" style={{ fontSize: 11 }}>
                  {new Date(c.created_at.replace(' ', 'T') + 'Z').toLocaleDateString('fr-CH')}
                  {c.created_by_name ? ` · ${c.created_by_name}` : ''}
                </div>
                {!selectable && (
                  <div style={{ display: 'flex', gap: 6, marginTop: 8 }}>
                    <button
                      className="btn btn-ghost"
                      style={{ padding: '3px 8px', fontSize: 11 }}
                      onClick={(e) => {
                        e.stopPropagation();
                        handleRename(c);
                      }}
                    >
                      ✎
                    </button>
                    <button
                      className="btn btn-ghost"
                      style={{ padding: '3px 8px', fontSize: 11, color: '#b84b2a' }}
                      onClick={(e) => {
                        e.stopPropagation();
                        handleDelete(c.id);
                      }}
                    >
                      🗑
                    </button>
                  </div>
                )}
              </div>
            </div>
          );
        })}
      </div>

      {preview && (
        <div
          className="ifc-capture-modal"
          onClick={() => setPreview(null)}
          style={{ cursor: 'pointer' }}
        >
          <div
            className="ifc-capture-dialog"
            style={{ maxWidth: 1100, cursor: 'default' }}
            onClick={(e) => e.stopPropagation()}
          >
            <div className="ifc-capture-head">
              <strong>{preview.title}</strong>
              <button className="btn btn-ghost" onClick={() => setPreview(null)}>
                ×
              </button>
            </div>
            <div className="ifc-capture-preview" style={{ background: '#111' }}>
              {preview.kind === 'turntable' ||
              (preview.mime_type ?? '').startsWith('video') ? (
                <video
                  src={fileUrl(preview.file_key)}
                  controls
                  autoPlay
                  loop
                  style={{ maxWidth: '100%', maxHeight: '75vh' }}
                />
              ) : (
                <img
                  src={fileUrl(preview.file_key)}
                  alt={preview.title}
                  style={{ maxWidth: '100%', maxHeight: '75vh' }}
                />
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
